import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { accountsAPI } from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Shield, Clock, CheckCircle2, XCircle, AlertTriangle,
  Loader2, ChevronRight, ArrowDownRight, ArrowUpRight,
  Lock, RefreshCw, FileText, Ban, Play, Eye, RotateCcw,
} from 'lucide-react';

// ── Status config ────────────────────────────────────
const STATUS_CONFIG = {
  PENDING:   { label: 'Pending',   color: 'bg-amber-400/10 text-amber-400 border-amber-400/20', icon: Clock },
  APPROVED:  { label: 'Approved',  color: 'bg-blue-400/10 text-blue-400 border-blue-400/20', icon: CheckCircle2 },
  EXECUTING: { label: 'Executing', color: 'bg-indigo-400/10 text-indigo-400 border-indigo-400/20', icon: Loader2 },
  EXECUTED:  { label: 'Executed',  color: 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20', icon: CheckCircle2 },
  RESTORED:  { label: 'Restored',  color: 'bg-cyan-400/10 text-cyan-400 border-cyan-400/20', icon: RotateCcw },
  REJECTED:  { label: 'Rejected',  color: 'bg-rose-400/10 text-rose-400 border-rose-400/20', icon: XCircle },
  FAILED:    { label: 'Failed',    color: 'bg-rose-400/10 text-rose-400 border-rose-400/20', icon: AlertTriangle },
  CANCELLED: { label: 'Cancelled', color: 'bg-zinc-400/10 text-zinc-400 border-zinc-400/20', icon: Ban },
};

const ENTITY_LABELS = {
  SALES_UPLOAD: 'Sales Upload',
  EXPENSE_EXCLUSION: 'Expense Exclusion',
  MANUAL_ADJUSTMENT: 'Manual Adjustment',
};

// ── Helpers ──────────────────────────────────────────
function fmtCurrency(v) {
  if (!v && v !== 0) return '—';
  const sign = v < 0 ? '-' : v > 0 ? '+' : '';
  return `${sign}₹${Math.abs(v).toLocaleString('en-IN')}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={cn('gap-1.5', cfg.color)} data-testid={`status-badge-${status}`}>
      <Icon className={cn('w-3 h-3', status === 'EXECUTING' && 'animate-spin')} />
      {cfg.label}
    </Badge>
  );
}

// ── Impact Preview Card ──────────────────────────────
function ImpactPreview({ snapshot }) {
  if (!snapshot) return null;
  const s = snapshot.summary || {};
  const f = snapshot.forecast_impact || {};

  const impactItems = [
    { label: 'Revenue', value: s.revenue_change, warn: s.revenue_change < 0 },
    { label: 'Expense', value: s.expense_change, warn: false },
    { label: 'Gross Profit', value: s.gross_profit_change, warn: s.gross_profit_change < 0 },
    { label: 'Margin (bps)', value: s.gross_margin_change_bps, isBps: true, warn: s.gross_margin_change_bps < 0 },
    { label: 'Cash Impact', value: s.cash_in_hand_change, warn: s.cash_in_hand_change < 0 },
    { label: 'Transactions', value: s.transaction_count, isCount: true },
    { label: 'Stl Matches', value: s.settlement_matches_affected, isCount: true, warn: s.settlement_matches_affected > 0 },
  ].filter(m => m.value !== undefined && m.value !== null);

  return (
    <div className="space-y-4" data-testid="impact-preview">
      <h4 className="font-manrope font-semibold text-sm text-foreground tracking-tight">Financial Impact Preview</h4>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {impactItems.map((m) => (
          <div key={m.label} className="bg-background/50 rounded-lg border border-border/50 p-3">
            <p className="text-xs text-muted-foreground mb-1">{m.label}</p>
            <p className={cn('font-mono text-sm tabular-nums', m.warn ? 'text-rose-400' : 'text-foreground')}>
              {m.isCount ? m.value : m.isBps ? `${m.value > 0 ? '+' : ''}${m.value || 0} bps` : fmtCurrency(m.value)}
            </p>
          </div>
        ))}
      </div>

      {/* Forecast impact */}
      {f.target_gap_change !== 0 && (
        <div className="bg-amber-400/5 border border-amber-400/20 rounded-lg p-3">
          <p className="text-xs text-amber-400 font-medium mb-1">Forecast Impact</p>
          <div className="flex items-center gap-4 text-sm">
            {f.landing_status_before && (
              <span className="flex items-center gap-1 text-muted-foreground">
                {f.landing_status_before} <ChevronRight className="w-3 h-3" /> <span className="text-amber-400">{f.landing_status_after}</span>
              </span>
            )}
            <span className="font-mono text-rose-400 tabular-nums">{fmtCurrency(f.target_gap_change)} gap</span>
          </div>
        </div>
      )}

      {/* Alerts expected */}
      {snapshot.alerts_expected_to_change?.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Alerts affected:</span>
          {snapshot.alerts_expected_to_change.map((a) => (
            <Badge key={a} variant="outline" className="text-xs bg-rose-400/10 text-rose-400 border-rose-400/20">{a}</Badge>
          ))}
        </div>
      )}

      {/* Locked period warning */}
      {snapshot.affects_locked_period && (
        <div className="flex items-center gap-2 bg-rose-400/10 border border-rose-400/20 rounded-lg p-3" data-testid="locked-period-warning">
          <Lock className="w-4 h-4 text-rose-400 shrink-0" />
          <p className="text-sm text-rose-400">
            {snapshot.locked_period_behavior === 'BLOCKED'
              ? 'This deletion affects a locked tax period and is BLOCKED.'
              : 'Warning: This deletion affects a locked tax period.'}
          </p>
        </div>
      )}

      {/* Periods affected */}
      {snapshot.pnl_periods_affected?.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Periods:</span>
          {snapshot.pnl_periods_affected.map((p, i) => (
            <Badge key={`${p.period_type}-${i}`} variant="outline" className="text-xs">{p.period_type}: {p.date || p.month}</Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Recompute Results Card ───────────────────────────
function RecomputeResults({ result }) {
  if (!result) return null;
  const stages = ['pnl', 'settlement_matching', 'forecast', 'alerts', 'pulse'];

  return (
    <div className="space-y-3" data-testid="recompute-results">
      <h4 className="font-manrope font-semibold text-sm text-foreground tracking-tight">Recompute Pipeline</h4>
      <div className="space-y-2">
        {stages.map((stage) => {
          const r = result[stage];
          if (!r) return null;
          const ok = r.status === 'COMPLETED';
          return (
            <div key={stage} className="flex items-center justify-between bg-background/50 rounded-lg border border-border/50 px-3 py-2">
              <div className="flex items-center gap-2">
                {ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />}
                <span className="text-sm font-medium capitalize">{stage}</span>
              </div>
              <span className={cn('text-xs font-mono', ok ? 'text-emerald-400' : 'text-amber-400')}>{r.status}</span>
            </div>
          );
        })}
        {result.execution_time_ms != null && (
          <p className="text-xs text-muted-foreground text-right font-mono">{result.execution_time_ms}ms total</p>
        )}
        {result.failure_point && (
          <p className="text-xs text-rose-400">{result.failure_point}</p>
        )}
      </div>
    </div>
  );
}

// ── Request Detail Modal ─────────────────────────────
function RequestDetailModal({ request, open, onClose, onAction }) {
  const [rejectionReason, setRejectionReason] = useState('');
  const [restoreReason, setRestoreReason] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showRestoreForm, setShowRestoreForm] = useState(false);

  if (!request) return null;

  const handleAction = async (action) => {
    setActionLoading(action);
    try {
      if (action === 'reject') {
        if (!rejectionReason.trim()) { toast.error('Rejection reason is required'); setActionLoading(null); return; }
        await accountsAPI.rejectDeleteRequest(request.request_id, { rejection_reason: rejectionReason });
        toast.success('Request rejected');
      } else if (action === 'approve') {
        await accountsAPI.approveDeleteRequest(request.request_id);
        toast.success('Request approved');
      } else if (action === 'execute') {
        await accountsAPI.executeDeleteRequest(request.request_id);
        toast.success('Deletion executed successfully');
      } else if (action === 'cancel') {
        await accountsAPI.cancelDeleteRequest(request.request_id);
        toast.success('Request cancelled');
      } else if (action === 'restore') {
        if (!restoreReason.trim()) { toast.error('Restore reason is required'); setActionLoading(null); return; }
        await accountsAPI.restoreDeleteRequest(request.request_id, { restore_reason: restoreReason });
        toast.success('Entity restored successfully');
      }
      setShowRejectForm(false);
      setShowRestoreForm(false);
      setRejectionReason('');
      setRestoreReason('');
      onAction();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Action failed: ${action}`);
    } finally {
      setActionLoading(null);
    }
  };

  const canApprove = request.status === 'PENDING';
  const canReject = request.status === 'PENDING';
  const canExecute = request.status === 'APPROVED';
  const canCancel = request.status === 'PENDING' || request.status === 'APPROVED';
  const canRestore = request.status === 'EXECUTED';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="request-detail-modal">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="font-manrope tracking-tight">Deletion Request</DialogTitle>
            <StatusBadge status={request.status} />
          </div>
          <DialogDescription className="font-mono text-xs">{request.request_id}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Entity summary */}
          <div className="bg-card/50 border border-border/50 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">{ENTITY_LABELS[request.entity_type] || request.entity_type}</Badge>
              <Badge variant="outline" className="text-xs">{request.action_type}</Badge>
            </div>
            <p className="text-sm font-medium">{request.entity_summary?.label || request.entity_id}</p>
            <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
              <div><span className="text-foreground/60">Reason:</span> <span className="text-foreground">{request.reason}</span></div>
              <div><span className="text-foreground/60">Requested:</span> <span className="text-foreground">{fmtDate(request.requested_at)}</span></div>
              <div><span className="text-foreground/60">By:</span> <span className="text-foreground">{request.requested_by_email || request.requested_by}</span></div>
              {request.approved_at && <div><span className="text-foreground/60">Approved:</span> <span className="text-foreground">{fmtDate(request.approved_at)}</span></div>}
              {request.approved_by_email && <div><span className="text-foreground/60">Approved by:</span> <span className="text-foreground">{request.approved_by_email}</span></div>}
              {request.executed_at && <div><span className="text-foreground/60">Executed:</span> <span className="text-foreground">{fmtDate(request.executed_at)}</span></div>}
              {request.rejected_at && <div><span className="text-foreground/60">Rejected:</span> <span className="text-foreground">{fmtDate(request.rejected_at)} by {request.rejected_by_email}</span></div>}
              {request.rejection_reason && <div className="col-span-2"><span className="text-foreground/60">Rejection reason:</span> <span className="text-rose-400">{request.rejection_reason}</span></div>}
              {request.failure_reason && <div className="col-span-2"><span className="text-foreground/60">Failure:</span> <span className="text-rose-400">{request.failure_reason}</span></div>}
            </div>
          </div>

          {/* Impact Preview */}
          <div className="bg-card/50 border border-border/50 rounded-xl p-4">
            <ImpactPreview snapshot={request.impact_snapshot} />
          </div>

          {/* Execution Result */}
          {request.execution_result && (
            <div className="bg-card/50 border border-border/50 rounded-xl p-4 space-y-2">
              <h4 className="font-manrope font-semibold text-sm tracking-tight">Execution Result</h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-muted-foreground">Mutation:</span> <span className={request.execution_result.entity_mutation === 'SUCCESS' ? 'text-emerald-400' : 'text-rose-400'}>{request.execution_result.entity_mutation}</span></div>
                <div><span className="text-muted-foreground">Rows affected:</span> <span className="font-mono">{request.execution_result.rows_affected}</span></div>
                <div><span className="text-muted-foreground">Type:</span> <span>{request.execution_result.mutation_type}</span></div>
              </div>
            </div>
          )}

          {/* Recompute Result */}
          {request.recompute_result && (
            <div className="bg-card/50 border border-border/50 rounded-xl p-4">
              <RecomputeResults result={request.recompute_result} />
            </div>
          )}

          {/* Reject Form */}
          {showRejectForm && (
            <div className="space-y-3" data-testid="reject-form">
              <Textarea
                placeholder="Reason for rejection..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="min-h-[80px]"
                data-testid="rejection-reason-input"
              />
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" size="sm" onClick={() => setShowRejectForm(false)}>Cancel</Button>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={actionLoading === 'reject'}
                  onClick={() => handleAction('reject')}
                  data-testid="confirm-reject-btn"
                >
                  {actionLoading === 'reject' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Confirm Reject
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-2">
          {canCancel && (
            <Button
              variant="outline"
              size="sm"
              disabled={!!actionLoading}
              onClick={() => handleAction('cancel')}
              data-testid="cancel-request-btn"
            >
              {actionLoading === 'cancel' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Ban className="w-4 h-4 mr-1" />}
              Cancel Request
            </Button>
          )}
          {canReject && !showRejectForm && (
            <Button
              variant="outline"
              size="sm"
              className="text-rose-400 border-rose-400/30 hover:bg-rose-400/10"
              onClick={() => setShowRejectForm(true)}
              data-testid="reject-request-btn"
            >
              <XCircle className="w-4 h-4 mr-1" /> Reject
            </Button>
          )}
          {canApprove && (
            <Button
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={!!actionLoading}
              onClick={() => handleAction('approve')}
              data-testid="approve-request-btn"
            >
              {actionLoading === 'approve' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Approve
            </Button>
          )}
          {canExecute && (
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={!!actionLoading}
              onClick={() => handleAction('execute')}
              data-testid="execute-request-btn"
            >
              {actionLoading === 'execute' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
              Execute Deletion
            </Button>
          )}
          {canRestore && !showRestoreForm && (
            <Button
              variant="outline"
              size="sm"
              className="text-cyan-400 border-cyan-400/30 hover:bg-cyan-400/10"
              onClick={() => setShowRestoreForm(true)}
              data-testid="restore-request-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1" /> Restore
            </Button>
          )}
          {showRestoreForm && (
            <div className="w-full space-y-2 pt-2 border-t border-border/30">
              <Textarea
                placeholder="Reason for restoring this entity..."
                value={restoreReason}
                onChange={(e) => setRestoreReason(e.target.value)}
                className="min-h-[60px]"
                data-testid="restore-reason-input"
              />
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setShowRestoreForm(false); setRestoreReason(''); }}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="bg-cyan-600 hover:bg-cyan-700 text-white"
                  disabled={actionLoading === 'restore' || !restoreReason.trim()}
                  onClick={() => handleAction('restore')}
                  data-testid="confirm-restore-btn"
                >
                  {actionLoading === 'restore' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}
                  Confirm Restore
                </Button>
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ────────────────────────────────────────
export default function DeletionRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(null);
  const [entityFilter, setEntityFilter] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [page, setPage] = useState(0);
  const LIMIT = 20;

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * LIMIT, limit: LIMIT };
      if (statusFilter) params.status = statusFilter;
      if (entityFilter) params.entity_type = entityFilter;
      const { data } = await accountsAPI.listDeleteRequests(params);
      setRequests(data.requests || []);
      setTotal(data.total || 0);
      setStatusCounts(data.status_counts || {});
    } catch (err) {
      toast.error('Failed to load deletion requests');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, entityFilter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const openDetail = (req) => {
    setSelectedRequest(req);
    setDetailOpen(true);
  };

  const handleActionComplete = () => {
    setDetailOpen(false);
    setSelectedRequest(null);
    fetchRequests();
  };

  const totalPending = (statusCounts.PENDING || 0) + (statusCounts.APPROVED || 0);

  return (
    <div className="space-y-6" data-testid="deletion-requests-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-manrope font-bold tracking-tight" data-testid="page-title">Deletion Requests</h1>
          <p className="text-sm text-muted-foreground mt-1">Financial deletion governance with Maker-Checker approval</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchRequests} disabled={loading} data-testid="refresh-btn">
          <RefreshCw className={cn('w-4 h-4 mr-1', loading && 'animate-spin')} /> Refresh
        </Button>
      </div>

      {/* Status summary strip */}
      <div className="grid grid-cols-3 md:grid-cols-7 gap-2" data-testid="status-counts">
        {Object.entries(STATUS_CONFIG).map(([status, cfg]) => {
          const count = statusCounts[status] || 0;
          const Icon = cfg.icon;
          const active = statusFilter === status;
          return (
            <button
              key={status}
              onClick={() => setStatusFilter(active ? null : status)}
              className={cn(
                'flex items-center gap-2 px-3 py-2.5 rounded-lg border text-left transition-all duration-200',
                active
                  ? 'border-foreground/30 bg-foreground/5'
                  : 'border-border/50 bg-card/30 hover:bg-card/60',
              )}
              data-testid={`status-filter-${status}`}
            >
              <Icon className={cn('w-3.5 h-3.5 shrink-0', cfg.color.split(' ')[1], status === 'EXECUTING' && 'animate-spin')} />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">{cfg.label}</p>
                <p className="font-mono text-sm font-semibold tabular-nums">{count}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Entity type filter */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Entity:</span>
        {[null, 'SALES_UPLOAD', 'EXPENSE_EXCLUSION'].map((et) => (
          <Button
            key={et || 'all'}
            variant={entityFilter === et ? 'default' : 'outline'}
            size="sm"
            className="h-7 text-xs"
            onClick={() => { setEntityFilter(et); setPage(0); }}
            data-testid={`entity-filter-${et || 'all'}`}
          >
            {et ? ENTITY_LABELS[et] : 'All'}
          </Button>
        ))}
      </div>

      {/* Pending actions banner */}
      {totalPending > 0 && (
        <div className="flex items-center gap-3 bg-amber-400/5 border border-amber-400/20 rounded-xl px-4 py-3" data-testid="pending-banner">
          <Shield className="w-5 h-5 text-amber-400 shrink-0" />
          <p className="text-sm text-amber-400">
            <span className="font-semibold">{totalPending}</span> request{totalPending > 1 ? 's' : ''} awaiting action
            ({statusCounts.PENDING || 0} pending review, {statusCounts.APPROVED || 0} approved awaiting execution)
          </p>
        </div>
      )}

      {/* Requests list */}
      <div className="space-y-2" data-testid="requests-list">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground" data-testid="empty-state">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No deletion requests found</p>
            <p className="text-xs mt-1">Deletion requests are created from entity detail views (Sales Uploads, Bills)</p>
          </div>
        ) : (
          requests.map((req) => {
            const summary = req.impact_snapshot?.summary || {};
            const revenueChange = summary.revenue_change || 0;
            const expenseChange = summary.expense_change || 0;
            const hasNegativeImpact = revenueChange < 0 || summary.gross_profit_change < 0;

            return (
              <button
                key={req.request_id}
                onClick={() => openDetail(req)}
                className="w-full text-left bg-card/30 border border-border/50 rounded-xl p-4 hover:border-border/80 hover:bg-card/50 transition-all duration-200 group"
                data-testid={`request-row-${req.request_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge status={req.status} />
                      <Badge variant="outline" className="text-xs">{ENTITY_LABELS[req.entity_type] || req.entity_type}</Badge>
                      {req.impact_snapshot?.affects_locked_period && (
                        <Badge variant="outline" className="text-xs bg-rose-400/10 text-rose-400 border-rose-400/20 gap-1">
                          <Lock className="w-2.5 h-2.5" /> Locked Period
                        </Badge>
                      )}
                    </div>

                    <p className="text-sm font-medium truncate">{req.entity_summary?.label || req.entity_id}</p>

                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{fmtDate(req.requested_at)}</span>
                      <span>{req.requested_by_email}</span>
                      {req.reason && <span className="truncate max-w-[200px]">"{req.reason}"</span>}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 shrink-0">
                    {/* Financial impact summary */}
                    <div className="text-right space-y-1">
                      {revenueChange !== 0 && (
                        <div className="flex items-center gap-1 justify-end">
                          <ArrowDownRight className="w-3 h-3 text-rose-400" />
                          <span className="font-mono text-xs text-rose-400 tabular-nums">{fmtCurrency(revenueChange)}</span>
                        </div>
                      )}
                      {expenseChange !== 0 && (
                        <div className="flex items-center gap-1 justify-end">
                          <ArrowUpRight className="w-3 h-3 text-emerald-400" />
                          <span className="font-mono text-xs text-emerald-400 tabular-nums">{fmtCurrency(expenseChange)}</span>
                        </div>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {total > LIMIT && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-muted-foreground">
            Showing {page * LIMIT + 1}-{Math.min((page + 1) * LIMIT, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="prev-page-btn">Previous</Button>
            <Button variant="outline" size="sm" disabled={(page + 1) * LIMIT >= total} onClick={() => setPage((p) => p + 1)} data-testid="next-page-btn">Next</Button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <RequestDetailModal
        request={selectedRequest}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onAction={handleActionComplete}
      />
    </div>
  );
}
